import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Button, IconButton } from '@mui/material';
import MapPatner from './MapPatner';
import Cronometro from './CronometroSocorrista';
import { useNavigate } from 'react-router-dom';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { calcularDistanciaTotalKm } from './MapPatner';
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

interface Chamado {
  id: string;
  cliente_id: string;
  status: string;
  data_abertura: string;
  localizacao: string;
  posicao_inicial_socorrista?: string;
  endereco_textual?: string;
}

// Função para calcular distância Haversine
const calcularDistanciaHaversine = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Raio da Terra em km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const EmergencySocorrista: React.FC = () => {
  const [chamado, setChamado] = useState<Chamado | null>(null);
  const [endereco, setEndereco] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [routeCoords, setRouteCoords] = useState<{ lat: number; lng: number }[]>([]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [socorristaPos, setSocorristaPos] = useState({ lat: 0, lng: 0 });
  const [currentRouteCoords, setCurrentRouteCoords] = useState<{ lat: number; lng: number }[]>([]);
  const [routeDuration, setRouteDuration] = useState<number | null>(null);
  const [distanciaRota, setDistanciaRota] = useState<number | null>(null);
  const [tempoEstimado60, setTempoEstimado60] = useState<number | null>(null);
  const [tempoEstimadoSimulacao, setTempoEstimadoSimulacao] = useState<number | null>(null);
  const [chegou, setChegou] = useState(false);
  const [simulacaoAtiva, setSimulacaoAtiva] = useState(false);
  const [intervalSimulacao, setIntervalSimulacao] = useState<NodeJS.Timeout | null>(null);
  const [trackingAtivo, setTrackingAtivo] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const lastSocorristaPos = useRef<{ lat: number; lng: number } | null>(null);
  const lastDBUpdate = useRef<number>(0);
  const MIN_DB_UPDATE_INTERVAL = 10000; // 10 segundos entre atualizações
  const navigate = useNavigate();

  // Rate limiting para API OSRM
  const lastOSRMCall = useRef<number>(0);
  const MIN_OSRM_INTERVAL = 30000;
  const isOSRMCallInProgress = useRef<boolean>(false);

  // Função para obter valor do storage (mobile ou web)
  const getStorageValue = async (key: string): Promise<string | null> => {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value;
        } else {
      return localStorage.getItem(key);
    }
  };

  // Função para salvar valor no storage (mobile ou web)
  const setStorageValue = async (key: string, value: string): Promise<void> => {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  };

  // Função para remover valor do storage (mobile ou web)
  const removeStorageValue = async (key: string): Promise<void> => {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  };

  // Função para atualizar posição no banco
  const updatePositionInDatabase = useCallback(async (lat: number, lng: number) => {
    if (!chamado?.id) {
      console.log('[EmergencySocorrista] Sem chamado ID para atualizar posição');
      return;
    }
    
    const now = Date.now();
    if (now - lastDBUpdate.current < MIN_DB_UPDATE_INTERVAL) {
      console.log('[EmergencySocorrista] Rate limiting DB update - aguardando...');
      return;
    }
    
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
      const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
      
      if (!url || !serviceKey || !accessToken) {
        console.warn('[EmergencySocorrista] Configuração inválida para atualizar posição');
        return;
      }
      
      lastDBUpdate.current = now;
      
      console.log('[EmergencySocorrista] 🚀 Enviando posição para o banco:', { lat, lng, chamadoId: chamado.id });
      
      const response = await fetch(`${url}/rest/v1/chamado?id=eq.${chamado.id}`, {
        method: 'PATCH',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({ 
          posicao_inicial_socorrista: `${lat},${lng}`
          // Removido ultima_atualizacao_posicao pois pode não existir na tabela
        })
      });
      
      if (response.ok) {
        console.log('[EmergencySocorrista] ✅ Posição atualizada no banco:', { lat, lng });
      } else {
        const errorText = await response.text();
        console.warn('[EmergencySocorrista] ⚠️ Erro ao atualizar posição no banco:', response.status, errorText);
      }
    } catch (error) {
      console.error('[EmergencySocorrista] ❌ Erro ao atualizar posição no banco:', error);
    }
  }, [chamado?.id, MIN_DB_UPDATE_INTERVAL]);

  // Função para cancelar chamado
  const cancelarChamado = async () => {
    if (!chamado) {
      console.error('[CANCELAR] Nenhum chamado encontrado');
      alert('Nenhum chamado encontrado para cancelar');
      return;
    }

    console.log('[CANCELAR] ===== INICIANDO CANCELAMENTO =====');
    console.log('[CANCELAR] Chamado ID:', chamado.id);
    console.log('[CANCELAR] Plataforma:', Capacitor.isNativePlatform() ? 'Mobile Nativo' : 'Web');
    
    // Confirmar cancelamento
    const confirmCancel = window.confirm('Tem certeza que deseja cancelar este chamado?');
    if (!confirmCancel) {
      console.log('[CANCELAR] Cancelamento abortado pelo usuário');
      return;
    }

    setLoading(true);
    
    try {
      // Obter dados do storage
      const url = process.env.REACT_APP_SUPABASE_URL;
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
      const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
      
      console.log('[CANCELAR] Configurações obtidas:');
      console.log('[CANCELAR] - URL:', url ? 'Configurado' : 'Não configurado');
      console.log('[CANCELAR] - Service Key:', serviceKey ? 'Configurado' : 'Não configurado');
      console.log('[CANCELAR] - Access Token:', accessToken ? 'Encontrado' : 'Não encontrado');
      
      if (!url || !serviceKey) {
        const errorMsg = 'Erro de configuração: Variáveis de ambiente não definidas';
        console.error('[CANCELAR]', errorMsg);
        alert(errorMsg);
        return;
      }
      
      if (!accessToken) {
        const errorMsg = 'Token de acesso não encontrado. Faça login novamente.';
        console.error('[CANCELAR]', errorMsg);
        alert(errorMsg);
      return;
    }
    
      // Para chamados mock/teste, simular sucesso
      if (chamado.id === 'mock' || chamado.id === 'error') {
        console.log('[CANCELAR] Chamado mock detectado - simulando cancelamento');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simular delay
        await removeStorageValue('chamadoId');
        console.log('[CANCELAR] ✅ Chamado mock cancelado com sucesso');
        navigate('/partner-emergencies');
      return;
    }
    
      // Construir endpoint
      const endpoint = `${url}/rest/v1/chamado?id=eq.${chamado.id}`;
      console.log('[CANCELAR] Endpoint construído:', endpoint);
      
      // Preparar dados para PATCH
      const patchData = { 
        status: 'finalizado',
        data_fechamento: new Date().toISOString()
      };
      console.log('[CANCELAR] Dados para PATCH:', patchData);
      
      // Headers da requisição
      const headers = {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      };
      console.log('[CANCELAR] Headers preparados (sem dados sensíveis)');
      
      console.log('[CANCELAR] Fazendo requisição PATCH...');
      
      // Fazer requisição com timeout
      const controller = new AbortController();
      const requestTimeout = setTimeout(() => {
        controller.abort();
        console.error('[CANCELAR] Timeout na requisição (15 segundos)');
      }, 15000);
      
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(patchData),
        signal: controller.signal
      });
      
      clearTimeout(requestTimeout);
      
      console.log('[CANCELAR] Resposta recebida:');
      console.log('[CANCELAR] - Status:', response.status);
      console.log('[CANCELAR] - Status Text:', response.statusText);
      console.log('[CANCELAR] - OK:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[CANCELAR] Erro na resposta da API:', errorText);
        
        // Tentar fazer parse do JSON de erro
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error_description || errorData.message || errorMessage;
        } catch (parseError) {
          console.warn('[CANCELAR] Não foi possível fazer parse do erro JSON');
        }
        
        alert(`Erro ao cancelar chamado: ${errorMessage}`);
        return;
      }
      
      // Processar resposta de sucesso
      const responseData = await response.json();
      console.log('[CANCELAR] ✅ Chamado finalizado com sucesso!');
      console.log('[CANCELAR] Dados da resposta:', responseData);
      
      // Tentar criar log do chamado
      try {
        console.log('[CANCELAR] Tentando criar log do chamado...');
        const chamadoData = Array.isArray(responseData) ? responseData[0] : responseData;
        
        if (chamadoData && chamadoData.id) {
          const logData = {
            chamado_id: chamadoData.id,
            cliente_id: chamadoData.cliente_id,
            localizacao: chamadoData.localizacao,
            endereco_textual: chamadoData.endereco_textual,
            status: chamadoData.status,
            operador_id: chamadoData.operador_id,
            socorrista_id: chamadoData.socorrista_id,
            data_abertura: chamadoData.data_abertura,
            data_fechamento: chamadoData.data_fechamento,
            descricao: chamadoData.descricao,
            prioridade: chamadoData.prioridade,
            notificacao_familiares: chamadoData.notificacao_familiares,
            posicao_inicial_socorrista: chamadoData.posicao_inicial_socorrista
          };
          
          console.log('[CANCELAR] Dados do log preparados:', logData);
          
          const logResponse = await fetch(`${url}/rest/v1/log_chamado`, {
            method: 'POST',
            headers: {
              'apikey': serviceKey,
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(logData)
          });
          
          if (logResponse.ok) {
            console.log('[CANCELAR] ✅ Log criado com sucesso');
                } else {
            const logErrorText = await logResponse.text();
            console.warn('[CANCELAR] ⚠️ Erro ao criar log:', logErrorText);
                }
          } else {
          console.warn('[CANCELAR] ⚠️ Dados insuficientes para criar log');
        }
      } catch (logError) {
        console.warn('[CANCELAR] ⚠️ Erro ao criar log (não crítico):', logError);
      }
      
      // Limpar storage
      console.log('[CANCELAR] Limpando chamadoId do storage...');
      await removeStorageValue('chamadoId');
      
      // Parar tracking/simulação se ativo
      if (trackingAtivo && watchId !== null) {
        console.log('[CANCELAR] Parando tracking de geolocalização...');
        navigator.geolocation.clearWatch(watchId);
        setTrackingAtivo(false);
      }
      
      if (simulacaoAtiva && intervalSimulacao) {
        console.log('[CANCELAR] Parando simulação...');
        clearInterval(intervalSimulacao);
        setSimulacaoAtiva(false);
      }
      
      console.log('[CANCELAR] ✅ CANCELAMENTO CONCLUÍDO COM SUCESSO!');
      console.log('[CANCELAR] Redirecionando para /partner-emergencies...');
      
      // Mostrar mensagem de sucesso antes de redirecionar
      alert('Chamado cancelado com sucesso!');
      navigate('/partner-emergencies');
      
    } catch (error: any) {
      console.error('[CANCELAR] ❌ ERRO DURANTE CANCELAMENTO:', error);
      
      let errorMessage = 'Erro desconhecido';
      if (error.name === 'AbortError') {
        errorMessage = 'Timeout na requisição. Verifique sua conexão.';
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Erro ao cancelar chamado: ${errorMessage}`);
    } finally {
      setLoading(false);
      console.log('[CANCELAR] ===== FIM DO PROCESSO DE CANCELAMENTO =====');
    }
  };

  useEffect(() => {
    // Timeout de segurança para garantir que a página carregue
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        console.warn('[EmergencySocorrista] Timeout de carregamento - forçando exibição da interface');
        setLoading(false);
        setPageError('Dados não carregaram completamente, mas a interface está disponível');
      }
    }, 10000); // 10 segundos timeout

    // Buscar chamado real pelo id salvo no storage
    const fetchChamado = async () => {
      try {
        console.log(`[EmergencySocorrista] Plataforma: ${Capacitor.isNativePlatform() ? 'Mobile Nativo' : 'Web'}`);
        console.log(`[EmergencySocorrista] Tentativa ${retryCount + 1} de carregamento`);
        
        const chamadoId = await getStorageValue('chamadoId');
        console.log('[EmergencySocorrista] ChamadoId obtido do storage:', chamadoId);
        
        if (!chamadoId) {
          console.log('[EmergencySocorrista] Nenhum chamadoId encontrado - criando chamado mock para teste');
          // Fallback: criar chamado mock para evitar tela branca
          setChamado({
            id: 'mock',
            cliente_id: '1',
            status: 'A caminho',
            data_abertura: new Date().toISOString(),
            localizacao: '-23.5505,-46.6333', // São Paulo como fallback
            endereco_textual: 'Endereço não disponível - modo offline'
          });
          setLoading(false);
          clearTimeout(loadingTimeout);
          return;
        }

        const url = process.env.REACT_APP_SUPABASE_URL;
        const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
        const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
        
        console.log('[EmergencySocorrista] Configurações:', {
          url: !!url,
          serviceKey: !!serviceKey,
          accessToken: !!accessToken
        });

        if (!url || !serviceKey) {
          console.error('[EmergencySocorrista] Variáveis de ambiente não configuradas');
          setPageError('Configuração inválida');
          setLoading(false);
          clearTimeout(loadingTimeout);
          return;
        }

        if (!accessToken) {
          console.error('[EmergencySocorrista] Token de acesso não encontrado');
          setPageError('Token não encontrado');
          setLoading(false);
          clearTimeout(loadingTimeout);
          return;
        }

        const endpoint = `${url}/rest/v1/chamado?id=eq.${chamadoId}`;
        console.log('[EmergencySocorrista] Fazendo requisição para:', endpoint);

        // Timeout para a requisição fetch
        const controller = new AbortController();
        const fetchTimeout = setTimeout(() => {
          controller.abort();
        }, 8000); // 8 segundos timeout para fetch

        const response = await fetch(endpoint, {
          method: 'GET',
                headers: {
                  'apikey': serviceKey,
                  'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });

        clearTimeout(fetchTimeout);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[EmergencySocorrista] Erro na resposta da API:', response.status, errorText);
          
          // Fallback em caso de erro da API
          console.log('[EmergencySocorrista] Criando chamado fallback devido a erro da API');
          setChamado({
            id: chamadoId,
            cliente_id: '1',
            status: 'A caminho',
            data_abertura: new Date().toISOString(),
            localizacao: '-23.5505,-46.6333',
            endereco_textual: 'Erro ao carregar endereço'
          });
          setPageError(`Erro da API: ${response.status}`);
          setLoading(false);
          clearTimeout(loadingTimeout);
          return;
        }

        const data = await response.json();
        console.log('[EmergencySocorrista] Dados recebidos:', data);

        if (Array.isArray(data) && data.length > 0) {
          const chamadoData = data[0];
          console.log('[EmergencySocorrista] Chamado carregado:', chamadoData);
          
          // Ordenar por data de criação - mais recente primeiro
          const chamadosOrdenados = data.sort((a: any, b: any) => {
            if (a.data_abertura && b.data_abertura) {
              return new Date(b.data_abertura).getTime() - new Date(a.data_abertura).getTime();
            }
            return (b.id || 0) - (a.id || 0);
          });
          setChamado(chamadosOrdenados[0]);
          setPageError(null);
        } else {
          console.log('[EmergencySocorrista] Nenhum chamado encontrado - criando fallback');
          setChamado({
            id: chamadoId,
            cliente_id: '1',
            status: 'A caminho',
            data_abertura: new Date().toISOString(),
            localizacao: '-23.5505,-46.6333',
            endereco_textual: 'Chamado não encontrado na base de dados'
          });
          setPageError('Chamado não encontrado');
        }
      } catch (error) {
        console.error('[EmergencySocorrista] Erro ao buscar chamado:', error);
        
        // Retry automático até 3 tentativas
        if (retryCount < 2) {
          console.log(`[EmergencySocorrista] Tentando novamente em 3 segundos... (tentativa ${retryCount + 2}/3)`);
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 3000);
          return;
        }
        
        // Fallback final após tentativas
        const chamadoId = await getStorageValue('chamadoId');
        console.log('[EmergencySocorrista] Criando chamado fallback final após erro');
        setChamado({
          id: chamadoId || 'error',
          cliente_id: '1',
          status: 'A caminho',
          data_abertura: new Date().toISOString(),
          localizacao: '-23.5505,-46.6333',
          endereco_textual: 'Erro de conexão - modo offline'
        });
        setPageError(`Erro de conexão: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      } finally {
        setLoading(false);
        clearTimeout(loadingTimeout);
      }
    };

    fetchChamado();

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, [retryCount]); // Dependência do retryCount para retry automático

  useEffect(() => {
    if (chamado && chamado.localizacao) {
      if (chamado.endereco_textual) {
        setEndereco(chamado.endereco_textual);
      } else {
        setEndereco('Endereço não disponível');
      }
      
      let posicaoInicial;
      if (chamado.posicao_inicial_socorrista) {
        const [socLat, socLng] = chamado.posicao_inicial_socorrista.split(',').map(Number);
        posicaoInicial = { lat: socLat, lng: socLng };
        setSocorristaPos(posicaoInicial);
      }
    }
  }, [chamado]);

  // useEffect para obter a localização atual do usuário
  useEffect(() => {
    if (!chamado || !chamado.localizacao) return;
    
    let watchId: number;
    
    if (navigator.geolocation) {
      const options = {
        enableHighAccuracy: true,
        timeout: 10000, // Aumentado para 10 segundos
        maximumAge: 30000 // Aceitar posição com até 30 segundos de idade
      };

      // Obter posição inicial
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPos = { 
            lat: position.coords.latitude, 
            lng: position.coords.longitude 
          };
          setSocorristaPos(newPos);
          console.log('[EmergencySocorrista] Localização inicial obtida:', newPos);
          
          // Enviar posição inicial para o banco
          updatePositionInDatabase(newPos.lat, newPos.lng);
        },
        (error) => {
          console.error('[EmergencySocorrista] Erro ao obter localização inicial:', error);
          
          // Traduzir e melhorar mensagens de erro
          let errorMessage = '';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permissão de localização negada. Verifique as configurações do navegador.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Localização temporariamente indisponível. Tentando novamente...';
              break;
            case error.TIMEOUT:
              errorMessage = 'Tempo limite excedido. Verificando posição salva...';
              break;
            default:
              errorMessage = 'Erro ao obter localização. Usando posição padrão.';
          }
          
          setGeoError(errorMessage);
          
          // Se for timeout, tentar usar posição salva ou padrão
          if (error.code === error.TIMEOUT) {
            console.log('[EmergencySocorrista] Timeout - tentando usar posição salva...');
            
            // Tentar usar posição inicial do socorrista se disponível
            if (chamado && chamado.posicao_inicial_socorrista) {
              const [lat, lng] = chamado.posicao_inicial_socorrista.split(',').map(Number);
              setSocorristaPos({ lat, lng });
              console.log('[EmergencySocorrista] Usando posição inicial como fallback:', { lat, lng });
            }
            
            // Limpar erro após 5 segundos
            setTimeout(() => setGeoError(null), 5000);
          }
        },
        options
      );

      // Monitorar mudanças de posição
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const newPos = { 
            lat: position.coords.latitude, 
            lng: position.coords.longitude 
          };
          setSocorristaPos(newPos);
          console.log('[EmergencySocorrista] Localização atualizada:', newPos);
          
          // Enviar posição atualizada para o banco (com rate limiting)
          updatePositionInDatabase(newPos.lat, newPos.lng);
        },
        (error) => {
          console.error('[EmergencySocorrista] Erro no watchPosition:', error);
          
          // Traduzir e melhorar mensagens de erro para watchPosition
          let errorMessage = '';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permissão de localização negada. Atualização automática desabilitada.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Localização temporariamente indisponível. Tentando novamente...';
              break;
            case error.TIMEOUT:
              errorMessage = 'Tempo limite excedido. Continuando com última posição conhecida.';
              break;
            default:
              errorMessage = 'Erro na atualização de localização.';
          }
          
          setGeoError(errorMessage);
          
          // Limpar erro após 3 segundos para não ser muito intrusivo
          setTimeout(() => setGeoError(null), 3000);
        },
        options
      );
      
      setWatchId(watchId);
    }

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [chamado, updatePositionInDatabase]);

  // useEffect para buscar a rota OSRM
  useEffect(() => {
    const fetchRoute = async () => {
      if (!socorristaPos || socorristaPos.lat === 0 || !chamado || !chamado.localizacao) {
        console.log('[EmergencySocorrista] Aguardando dados para buscar rota...');
        return;
      }

      const [destinoLat, destinoLng] = chamado.localizacao.split(',').map(Number);
      
      // Verificar rate limiting
      const now = Date.now();
      if (now - lastOSRMCall.current < MIN_OSRM_INTERVAL) {
        console.log('[EmergencySocorrista] Rate limiting OSRM - aguardando...');
        return;
      }
      
      if (isOSRMCallInProgress.current) {
        console.log('[EmergencySocorrista] Chamada OSRM já em progresso...');
        return;
      }

      isOSRMCallInProgress.current = true;
      lastOSRMCall.current = now;
      setLoadingRoute(true);

      const url = `https://router.project-osrm.org/route/v1/driving/${socorristaPos.lng},${socorristaPos.lat};${destinoLng},${destinoLat}?overview=full&geometries=geojson&steps=true`;
      
      try {
        console.log('[EmergencySocorrista] Buscando rota OSRM...');
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.routes && data.routes[0]) {
          const route = data.routes[0];
          const coords = route.geometry.coordinates.map(
            ([lng, lat]: [number, number]) => ({ lat, lng })
          );
          
          setRouteCoords(coords);
          setCurrentRouteCoords(coords);
          
          // Duração em minutos
          const duration = Math.ceil(route.duration / 60);
          setRouteDuration(duration);
          
          // Distância em km
          const distance = route.distance / 1000;
          setDistanciaRota(distance);
          
          // Tempo estimado a 60km/h
          const tempo60 = (distance / 60) * 60; // minutos
          setTempoEstimado60(Math.ceil(tempo60));
          
          console.log('[EmergencySocorrista] Rota obtida:', {
            distancia: `${distance.toFixed(2)} km`,
            duracao: `${duration} min`,
            pontos: coords.length
          });
        }
      } catch (error) {
        console.error('[EmergencySocorrista] Erro ao buscar rota:', error);
      } finally {
        setLoadingRoute(false);
        isOSRMCallInProgress.current = false;
      }
    };

    fetchRoute();
  }, [socorristaPos, chamado]);

  // Cleanup quando componente desmonta
  useEffect(() => {
    return () => {
      if (intervalSimulacao) {
        clearInterval(intervalSimulacao);
      }
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [intervalSimulacao, watchId]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      maxWidth: 800,
      mx: 'auto',
      minHeight: 'calc(100vh - 64px)',
      position: 'relative',
      overflow: 'hidden',
      pb: 2,
      mt: 3
    }}>
      {/* Paper de Informações do Chamado */}
      <Paper elevation={0} sx={{
        borderRadius: 2,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 'none',
        maxWidth: 800,
        mx: 'auto',
        mb: 2,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 3 },
        position: 'relative',
      }}>
        {/* Marca d'água */}
        <Box
          component="img"
          src={require('../../assets/Design sem nome (7).png')}
          alt="Marca d'água Support Life"
          sx={{
            position: 'absolute',
            top: '55%',
            left: 0,
            width: '100%',
            height: '60%',
            objectFit: 'contain',
            opacity: 0.07,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        
        <Typography variant="h6" sx={{ mb: 1, position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Informações do Chamado{chamado ? ` (ID: ${chamado.id})` : ''}</span>
          {/* Botão de retry manual se houver erro */}
          {pageError && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setRetryCount(0);
                setLoading(true);
                setPageError(null);
              }}
              sx={{ ml: 2 }}
            >
              Tentar Novamente
            </Button>
          )}
        </Typography>

        {/* Alerta de erro se houver */}
        {pageError && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <Typography variant="body2" color="warning.dark">
              ⚠️ {pageError}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              A funcionalidade básica está disponível mesmo com este erro.
            </Typography>
          </Box>
        )}

        {/* Indicador de erro de geolocalização */}
        {geoError && (
          <Box sx={{ 
            mt: 1, 
            p: 1.5, 
            backgroundColor: 'rgba(255, 193, 7, 0.1)', 
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: 1,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Typography variant="caption" sx={{ 
              color: 'warning.dark',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              📍 {geoError}
            </Typography>
          </Box>
        )}

        {chamado ? (
          <>
            {/* SEMPRE exibe o mapa, mesmo que simples */}
            {(() => {
              // Usar a posição atual do socorrista em vez da posição inicial
              let socorristaLat: number | null = null;
              let socorristaLng: number | null = null;
              
              // Priorizar a posição atual do socorrista (GPS em tempo real)
              if (socorristaPos && socorristaPos.lat !== 0 && socorristaPos.lng !== 0) {
                socorristaLat = socorristaPos.lat;
                socorristaLng = socorristaPos.lng;
                console.log('[EmergencySocorrista] Usando posição atual do socorrista:', { lat: socorristaLat, lng: socorristaLng });
              } 
              // Fallback para posição inicial se não tiver GPS
              else if (chamado && chamado.posicao_inicial_socorrista) {
                const parts = chamado.posicao_inicial_socorrista.split(',');
                if (parts.length === 2) {
                  socorristaLat = Number(parts[0]);
                  socorristaLng = Number(parts[1]);
                  console.log('[EmergencySocorrista] Usando posição inicial do socorrista (fallback):', { lat: socorristaLat, lng: socorristaLng });
                }
              }
              
              // Verificar se chamado e localizacao existem
              if (!chamado || !chamado.localizacao) {
                console.error('[EmergencySocorrista] Chamado ou localização não encontrados');
                return (
                  <Box sx={{ width: '100%', minHeight: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="error">Erro: Dados do chamado não encontrados</Typography>
                  </Box>
                );
              }
              
              // Extrair coordenadas do chamado com segurança
              const chamadoCoords = chamado.localizacao.split(',');
              if (chamadoCoords.length !== 2) {
                console.error('[EmergencySocorrista] Formato de localização inválido:', chamado.localizacao);
                return (
                  <Box sx={{ width: '100%', minHeight: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="error">Erro: Formato de localização inválido</Typography>
                  </Box>
                );
              }
              
              const chamadoLat = Number(chamadoCoords[0]);
              const chamadoLng = Number(chamadoCoords[1]);
              
              // Verificar se as coordenadas são válidas
              if (isNaN(chamadoLat) || isNaN(chamadoLng) || 
                  chamadoLat < -90 || chamadoLat > 90 || 
                  chamadoLng < -180 || chamadoLng > 180) {
                console.error('[EmergencySocorrista] Coordenadas do chamado inválidas:', { chamadoLat, chamadoLng });
                return (
                  <Box sx={{ width: '100%', minHeight: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="error">Erro: Coordenadas do chamado inválidas</Typography>
                  </Box>
                );
              }
              
              return (
                <Box sx={{ width: '100%', minHeight: 350, maxHeight: 500, position: 'relative', zIndex: 1 }}>
                  <MapPatner
                    key={`map-${chamadoLat}-${chamadoLng}-${socorristaLat}-${socorristaLng}`}
                    center={{
                      lat: chamadoLat,
                      lng: chamadoLng
                    }}
                    zoom={15}
                    markers={[{
                      position: {
                        lat: chamadoLat,
                        lng: chamadoLng
                      },
                      title: 'Local do chamado'
                    },
                    ...(socorristaLat !== null && socorristaLng !== null && 
                        !isNaN(socorristaLat) && !isNaN(socorristaLng) ? [{
                      position: {
                        lat: socorristaLat,
                        lng: socorristaLng
                      },
                      title: 'Sua localização'
                    }] : [])
                    ]}
                    routeCoords={currentRouteCoords && currentRouteCoords.length > 1 ? currentRouteCoords : routeCoords}
                    ambulancePosition={socorristaLat !== null && socorristaLng !== null && 
                        !isNaN(socorristaLat) && !isNaN(socorristaLng)
                      ? { lat: socorristaLat, lng: socorristaLng }
                      : undefined}
                  />
                  {loadingRoute && (
                    <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Box>
              );
            })()}
            
            {/* Seção atualizada com distância e tempo real */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              gap: 4, 
              mt: 2, 
              position: 'relative', 
              zIndex: 1 
            }}>
              {/* Distância */}
              <Box sx={{ textAlign: 'left', minWidth: 90 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {distanciaRota ? `${distanciaRota.toFixed(1)} km` : '--'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Distância
                </Typography>
              </Box>
              
              {/* Tempo estimado OSRM */}
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {routeDuration ? `${routeDuration} min` : '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tempo estimado
                  </Typography>
                </Box>
              </Box>
              
              {/* Status ou tempo alternativo */}
              <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {chegou ? 'No local' : 'A caminho'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ textAlign: 'left', color: 'text.secondary', mt: 1, position: 'relative', zIndex: 1 }}>
              {endereco || chamado.endereco_textual || 'Endereço não disponível'}
            </Typography>

            {/* SEMPRE exibe os botões de ação principais */}
            {!chegou && (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2, width: '100%', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: 120, fontWeight: 600 }}
                  onClick={async () => {
                    if (!chamado) return;
                    
                    // Para chamados mock/fallback, apenas simula
                    if (chamado.id === 'mock' || chamado.id === 'error') {
                      setChegou(true);
                      return;
                    }
                    
                    const url = process.env.REACT_APP_SUPABASE_URL;
                    const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
                    const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
                    
                    if (!url || !serviceKey) {
                      alert('Configuração inválida. Usando modo offline.');
                      setChegou(true);
                      return;
                    }
                    if (!accessToken) {
                      alert('Token não encontrado. Usando modo offline.');
                      setChegou(true);
                      return;
                    }
                    
                    setLoading(true);
                    try {
                      const response = await fetch(`${url}/rest/v1/chamado?id=eq.${chamado.id}`, {
                        method: 'PATCH',
                        headers: {
                          'apikey': serviceKey,
                          'Authorization': `Bearer ${accessToken}`,
                          'Content-Type': 'application/json',
                          'Prefer': 'return=minimal'
                        },
                        body: JSON.stringify({ status: 'Ambulância no local' })
                      });
                      
                      if (!response.ok) {
                        console.error('Erro ao atualizar status, mas continuando...');
                        setChegou(true);
                        return;
                      }

                      setChegou(true);
                    } catch (err: any) {
                      console.error('Erro ao atualizar status:', err);
                      setChegou(true); // Continua mesmo com erro
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Cheguei'}
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  sx={{ minWidth: 120, fontWeight: 600 }}
                  onClick={cancelarChamado}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Cancelar'}
                </Button>
              </Box>
            )}
            
            {/* Botão de conclusão sempre disponível */}
            {chegou && (
              <Box sx={{ position: 'fixed', left: 0, bottom: 0, width: '100%', zIndex: 2000, display: 'flex', justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  sx={{ minWidth: 220, fontWeight: 700, fontSize: 18, py: 1.5, borderRadius: 3, boxShadow: 3 }}
                  onClick={async () => {
                    if (!chamado) {
                      navigate('/partner-emergencies');
                      return;
                    }
                    
                    // Para chamados mock/fallback, apenas redireciona
                    if (chamado.id === 'mock' || chamado.id === 'error') {
                      await removeStorageValue('chamadoId');
                      navigate('/partner-emergencies');
                      return;
                    }
                    
                    const url = process.env.REACT_APP_SUPABASE_URL;
                    const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
                    const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
                    
                    if (!url || !serviceKey || !accessToken) {
                      await removeStorageValue('chamadoId');
                      navigate('/partner-emergencies');
                      return;
                    }
                    
                    setLoading(true);
                    try {
                      const response = await fetch(`${url}/rest/v1/chamado?id=eq.${chamado.id}`, {
                        method: 'PATCH',
                        headers: {
                          'apikey': serviceKey,
                          'Authorization': `Bearer ${accessToken}`,
                          'Content-Type': 'application/json',
                          'Prefer': 'return=minimal'
                        },
                        body: JSON.stringify({ 
                          status: 'concluído',
                          data_fechamento: new Date().toISOString()
                        })
                      });

                      if (!response.ok) {
                        console.error('Erro ao concluir chamado na API:', response.status);
                        // Mesmo com erro, vamos permitir sair para não travar o usuário
                      }
                    } catch (err: any) {
                      console.error('Erro ao concluir, mas continuando:', err);
                    } finally {
                      await removeStorageValue('chamadoId');
                      navigate('/partner-emergencies');
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Chamado concluído'}
                </Button>
              </Box>
            )}
          </>
        ) : (
          /* Fallback caso não tenha chamado - SEMPRE exibe algo */
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, position: 'relative', zIndex: 1 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Carregando informações do chamado...
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/partner-emergencies')}
              sx={{ mt: 2 }}
            >
              Voltar para Lista de Chamados
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EmergencySocorrista;